import React from "react";
import { useDocument } from "../../utils/DocumentContext";

/**
 * DocumentPrint — печать подробной информации по текущему документу
 * Отображает метаинформацию о документе, статистику и все позиции
 */
export default function DocumentPrint({ document = null, positions = [] }) {
    const {
        selectedDocument,
        documentPositions,
        documentStatus
    } = useDocument();

    // Используем переданные данные с приоритетом над контекстом
    const currentDocument = document || selectedDocument;
    const currentPositions = positions.length > 0 ? positions : documentPositions;

    console.log('DocumentPrint: Используемые данные:', {
        'Передан document': !!document,
        'Передан positions': positions.length,
        'Из контекста selectedDocument': !!selectedDocument,
        'Из контекста documentPositions': documentPositions.length,
        'Итоговый currentDocument': currentDocument?.filename,
        'currentPositions детали': currentPositions
    });

    // Если нет документа
    if (!currentDocument) {
        return (
            <div className="doc-wrap">
                <div className="doc-header">
                    <h1>НЕМАЄ ОБРАНОГО ДОКУМЕНТА</h1>
                    <p>Оберіть документ для відображення інформації</p>
                </div>
            </div>
        );
    }

    // Рассчитываем статистику для текущих позиций
    const totalPlan = currentPositions.reduce((s, x) => s + (Number(x.plan) || 0), 0);
    const totalFact = currentPositions.reduce((s, x) => s + (Number(x.fact) || 0), 0);

    // Вычисляем собственную статистику
    const stats = {
        total: currentPositions.length,
        processed: currentPositions.filter(pos => (Number(pos.fact) || 0) > 0).length,
        completed: currentPositions.filter(pos => {
            const plan = Number(pos.plan) || 0;
            const fact = Number(pos.fact) || 0;
            return fact >= plan && plan > 0;
        }).length
    };

    // Добавляем remaining
    stats.remaining = currentPositions.filter(pos => {
        const plan = Number(pos.plan) || 0;
        const fact = Number(pos.fact) || 0;
        return fact < plan;
    }).length;

    // Вычисляем проценты
    stats.processedPercent = stats.total > 0 ? Math.round((stats.processed / stats.total) * 100) : 0;
    stats.completionPercent = stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0;

    console.log('DocumentPrint: Вычисленная статистика:', stats);

    // Функция для получения статуса на русском
    const getStatusText = (status) => {
        const statusMap = {
            'new': 'Новый',
            'processing': 'В обработке',
            'processed': 'Обработан',
            'completed': 'Завершен'
        };
        return statusMap[status] || status;
    };

    // Функция для получения цвета статуса позиции
    const getPositionStatusColor = (position) => {
        const plan = Number(position.plan) || 0;
        const fact = Number(position.fact) || 0;

        if (fact === 0) return '#ffebee'; // Красноватый - не начато
        if (fact >= plan && plan > 0) return '#e8f5e8'; // Зеленоватый - завершено
        return '#fff3e0'; // Оранжевый - в процессе
    };

    return (
        <div className="doc-wrap">
            {/* Заголовок документа */}
            <div className="doc-header">
                <h1>ЗВІТ ПО ДОКУМЕНТУ</h1>
                <div className="doc-subtitle">{currentDocument.filename}</div>
                <div className="doc-date">Дата друку: {new Date().toLocaleString("uk-UA")}</div>
            </div>

            {/* Информация о документе */}
            <div className="doc-info-section">
                <h2>Інформація про документ</h2>
                <div className="doc-info-grid">
                    <div className="info-item">
                        <span className="info-label">Файл:</span>
                        <span className="info-value">{currentDocument.filename}</span>
                    </div>
                    <div className="info-item">
                        <span className="info-label">Створено:</span>
                        <span className="info-value">{new Date(currentDocument.created).toLocaleString("ru-RU")}</span>
                    </div>
                    <div className="info-item">
                        <span className="info-label">Розмір:</span>
                        <span className="info-value">{(currentDocument.size / 1024).toFixed(1)} КБ</span>
                    </div>
                    <div className="info-item">
                        <span className="info-label">Статус:</span>
                        <span className="info-value status-badge">{getStatusText(documentStatus)}</span>
                    </div>
                    <div className="info-item">
                        <span className="info-label">Обрано:</span>
                        <span className="info-value">{new Date(currentDocument.selectedAt).toLocaleString("ru-RU")}</span>
                    </div>
                </div>
            </div>

            {/* Статистика */}
            <div className="doc-stats-section">
                <h2>Статистика обробки</h2>
                <div className="stats-grid">
                    <div className="stat-card">
                        <div className="stat-number">{stats.total}</div>
                        <div className="stat-label">Всього позицій</div>
                    </div>
                    <div className="stat-card processed">
                        <div className="stat-number">{stats.processed}</div>
                        <div className="stat-label">Оброблено</div>
                        <div className="stat-percent">{stats.processedPercent}%</div>
                    </div>
                    <div className="stat-card completed">
                        <div className="stat-number">{stats.completed}</div>
                        <div className="stat-label">Завершено</div>
                        <div className="stat-percent">{stats.completionPercent}%</div>
                    </div>
                    <div className="stat-card remaining">
                        <div className="stat-number">{stats.remaining}</div>
                        <div className="stat-label">Залишилось</div>
                    </div>
                </div>

                <div className="quantity-summary">
                    <div className="qty-item">
                        <span className="qty-label">Планируемое количество:</span>
                        <span className="qty-value">{totalPlan}</span>
                    </div>
                    <div className="qty-item">
                        <span className="qty-label">Фактическое количество:</span>
                        <span className="qty-value">{totalFact}</span>
                    </div>
                    <div className="qty-item">
                        <span className="qty-label">Разница:</span>
                        <span className={`qty-value ${totalFact - totalPlan >= 0 ? 'positive' : 'negative'}`}>
                            {totalFact - totalPlan > 0 ? '+' : ''}{totalFact - totalPlan}
                        </span>
                    </div>
                </div>
            </div>

            {/* Таблица позиций */}
            <div className="doc-positions-section">
                <h2>Позиции документа ({currentPositions.length})</h2>

                {currentPositions.length === 0 ? (
                    <div className="no-data">Нет позиций в документе</div>
                ) : (
                    <table className="doc-table">
                        <thead>
                            <tr>
                                <th>№</th>
                                <th>Название</th>
                                <th>Артикул</th>
                                <th>Штрих-код</th>
                                <th>План</th>
                                <th>Факт</th>
                                <th>Статус</th>
                                <th>Обновлено</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentPositions.map((position, index) => {
                                const plan = Number(position.plan) || 0;
                                const fact = Number(position.fact) || 0;
                                const isCompleted = fact >= plan;
                                const isStarted = fact > 0;

                                return (
                                    <tr
                                        key={index}
                                        style={{ backgroundColor: getPositionStatusColor(position) }}
                                    >
                                        <td className="text-center">{index + 1}</td>
                                        <td className="position-name">{position.name || position.title || "—"}</td>
                                        <td className="text-center">{position.article || "—"}</td>
                                        <td className="barcode">{position.barcode || "—"}</td>
                                        <td className="text-center">{plan}</td>
                                        <td className="text-center">{fact}</td>
                                        <td className="text-center">
                                            <span className={`status-indicator ${
                                                isCompleted ? 'completed' : 
                                                isStarted ? 'in-progress' : 'not-started'
                                            }`}>
                                                {isCompleted ? '✓' : isStarted ? '◐' : '○'}
                                            </span>
                                        </td>
                                        <td className="text-center small-text">
                                            {position.updatedAt ?
                                                new Date(position.updatedAt).toLocaleString("ru-RU", {
                                                    day: '2-digit',
                                                    month: '2-digit',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                }) : '—'
                                            }
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            <style>{`
                .doc-wrap { 
                    background: #fff; 
                    color: #000; 
                    font-family: Arial, sans-serif;
                    max-width: 210mm;
                    margin: 0 auto;
                    padding: 10mm;
                }
                
                .doc-header { 
                    text-align: center; 
                    margin-bottom: 15px; 
                    border-bottom: 2px solid #000; 
                    padding-bottom: 10px; 
                }
                
                .doc-header h1 { 
                    margin: 0 0 5px 0; 
                    font-size: 20px; 
                    font-weight: bold;
                }
                
                .doc-subtitle {
                    font-size: 14px;
                    color: #333;
                    margin-bottom: 5px;
                    font-weight: 600;
                }
                
                .doc-date { 
                    font-size: 11px; 
                    color: #666; 
                }
                
                .doc-info-section,
                .doc-stats-section,
                .doc-positions-section {
                    margin-bottom: 20px;
                }
                
                .doc-info-section h2,
                .doc-stats-section h2,
                .doc-positions-section h2 {
                    font-size: 14px;
                    margin: 0 0 8px 0;
                    color: #333;
                    border-bottom: 1px solid #ddd;
                    padding-bottom: 3px;
                }
                
                .doc-info-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 8px;
                    margin-bottom: 10px;
                }
                
                .info-item {
                    display: flex;
                    justify-content: space-between;
                    padding: 4px 0;
                    border-bottom: 1px dotted #ccc;
                }
                
                .info-label {
                    font-weight: 600;
                    color: #555;
                }
                
                .info-value {
                    color: #000;
                }
                
                .status-badge {
                    background: #e3f2fd;
                    padding: 2px 6px;
                    border-radius: 3px;
                    font-size: 11px;
                }
                
                .stats-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 10px;
                    margin-bottom: 15px;
                }
                
                .stat-card {
                    text-align: center;
                    padding: 8px;
                    border: 1px solid #ddd;
                    border-radius: 4px;
                    background: #f8f9fa;
                }
                
                .stat-card.processed { background: #e8f4fd; }
                .stat-card.completed { background: #e8f5e8; }
                .stat-card.remaining { background: #fff3cd; }
                
                .stat-number {
                    font-size: 18px;
                    font-weight: bold;
                    color: #333;
                }
                
                .stat-label {
                    font-size: 10px;
                    color: #666;
                    margin-top: 2px;
                }
                
                .stat-percent {
                    font-size: 9px;
                    color: #888;
                }
                
                .quantity-summary {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 10px;
                    padding: 8px;
                    background: #f0f0f0;
                    border-radius: 4px;
                }
                
                .qty-item {
                    display: flex;
                    justify-content: space-between;
                    font-size: 11px;
                }
                
                .qty-label {
                    color: #555;
                }
                
                .qty-value {
                    font-weight: bold;
                }
                
                .qty-value.positive { color: #2e7d32; }
                .qty-value.negative { color: #d32f2f; }
                
                .doc-table { 
                    width: 100%; 
                    border-collapse: collapse; 
                    font-size: 10px;
                }
                
                .doc-table th, 
                .doc-table td { 
                    border: 1px solid #ccc; 
                    padding: 4px 6px; 
                    text-align: left;
                }
                
                .doc-table th { 
                    background: #f5f5f5; 
                    font-weight: bold;
                    font-size: 9px;
                }
                
                .text-center { text-align: center; }
                .small-text { font-size: 8px; }
                
                .position-name {
                    max-width: 120px;
                    word-wrap: break-word;
                }
                
                .barcode { 
                    font-family: monospace; 
                    font-size: 8px;
                }
                
                .status-indicator {
                    font-size: 12px;
                    font-weight: bold;
                }
                
                .status-indicator.completed { color: #2e7d32; }
                .status-indicator.in-progress { color: #f57c00; }
                .status-indicator.not-started { color: #d32f2f; }
                
                .no-data {
                    text-align: center;
                    padding: 20px;
                    color: #666;
                    font-style: italic;
                }
                
                @page { 
                    size: A4; 
                    margin: 10mm; 
                }
                
                @media print {
                    .doc-wrap {
                        max-width: none;
                        margin: 0;
                        padding: 0;
                    }
                }
            `}</style>
        </div>
    );
}
